import {
  Alert,
  Button,
  Checkbox,
  Col,
  Form,
  FormInstance,
  Modal,
  ModalProps,
  Row,
  Typography,
} from 'antd';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import moment, { Moment } from 'moment';
import { FloatingFormItem, InputDate } from '@/components/_common';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import {
  ComplementCloseFormValues,
  WorkOrderComplementCloseType,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { isAppPortocar } from '@/index';
import PortoIsValidCustomer, {
  PortoIsValidCustomerCallback,
} from '@/components/_common/PortoIsValidCustomer';
import { useState } from 'react';
import BenefitsInfoAlert from './BenefitsInfoAlert';

interface Props extends ModalProps {
  form: FormInstance<ComplementCloseFormValues>;
  hasReturnDate: boolean;
  onCancel: () => void;
  onConfirm: (closeType: WorkOrderComplementCloseType) => void;
  confirmLoadingCloseComplement: boolean;
  confirmLoadingCloseAllComplement: boolean;
}

const ComplementCloseModal: React.FC<Props> = ({
  form,
  onCancel,
  onConfirm,
  hasReturnDate,
  confirmLoadingCloseComplement,
  confirmLoadingCloseAllComplement,
  ...props
}) => {
  const {
    workorder: { data: workorder },
    complement: { data: complement },
  } = useWorkorderDetailContext();

  const isPortoBattery = !!workorder?.isPortoBatteryWorkOrder;

  const workorderNumber = String(workorder?.number).padStart(6, '0');
  const complementNumber = String(complement?.number).padStart(2, '0');

  const [portoIsValidCustomer, setPortoIsValidCustomer] =
    useState<PortoIsValidCustomerCallback>();

  const disableIfIsCustomerInvalid =
    !!portoIsValidCustomer &&
    (!!portoIsValidCustomer?.isLoading ||
      !portoIsValidCustomer?.isValidCustomer);

  return (
    <Modal
      width="60%"
      {...props}
      closable={false}
      destroyOnClose
      footer={[
        <SpaceForButton justifyContent="end">
          <Button block onClick={onCancel}>
            Cancelar
          </Button>

          <Button
            block
            type="primary"
            onClick={() => onConfirm(WorkOrderComplementCloseType.all)}
            loading={confirmLoadingCloseAllComplement}
            disabled={
              isPortoBattery ||
              confirmLoadingCloseComplement ||
              disableIfIsCustomerInvalid
            }
          >
            Fechar todos complementos
          </Button>

          <Button
            block
            type="primary"
            onClick={() => onConfirm(WorkOrderComplementCloseType.one)}
            loading={confirmLoadingCloseComplement}
            disabled={
              confirmLoadingCloseAllComplement || disableIfIsCustomerInvalid
            }
          >
            {`Fechar complemento ${workorderNumber}/${complementNumber}`}
          </Button>
        </SpaceForButton>,
      ]}
    >
      <>
        <Typography.Title level={5}>Fechar complemento</Typography.Title>
        <p>{`Deseja realmente fechar o complemento do orçamento "${workorderNumber}/${complementNumber}"?`}</p>

        <Form form={form}>
          <Row gutter={[12, 12]}>
            <Col span={24}>
              <Form.Item
                name="hasReturnDate"
                initialValue={false}
                valuePropName="checked"
                noStyle
              >
                <Checkbox>Preencher a data de entrega ao fechar</Checkbox>
              </Form.Item>
            </Col>

            <Col span={24}>
              {hasReturnDate && complement?.returnDate && (
                <Alert
                  type="warning"
                  description="Já existe uma data de entrega para este complemento, deseja substituir?"
                />
              )}

              {!hasReturnDate && !complement?.returnDate && (
                <Alert
                  type="warning"
                  description="Não será atualizado a data de entrega no complemento."
                />
              )}
            </Col>

            <Col span={24}>
              <FloatingFormItem
                name="closingDate"
                label="Data de fechamento"
                rules={[
                  () => ({
                    validator(_, value: Moment) {
                      if (
                        !value ||
                        value.isSameOrAfter(
                          moment(complement?.authorizationDate)?.milliseconds(
                            0,
                          ),
                        )
                      ) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error('Deve ser maior que a data de autorização'),
                      );
                    },
                  }),
                ]}
              >
                <InputDate
                  disabled={
                    isAppPortocar ||
                    !hasPermissions([
                      Authorities.MOVIMENTATION_WORKORDER_AUTHORIZED_EDIT_CLOSING_DATE,
                    ])
                  }
                  showTime
                />
              </FloatingFormItem>
            </Col>
          </Row>

          {isAppPortocar && (
            <>
              <PortoIsValidCustomer
                description="Para fazer o fechamento é obrigatório que os dados do cliente estejam completos"
                customerId={workorder?.customer?.id}
                onCallback={setPortoIsValidCustomer}
              />

              <BenefitsInfoAlert visible={!!props.open} />
            </>
          )}
        </Form>
      </>
    </Modal>
  );
};

export default ComplementCloseModal;
